import { API_BASE_URL } from "../../constant/constant";
import React, { useState } from "react";
import {
  Card,
  CardText,
  Title,
  PriceText,
  FromText,
  Features,
  FeatureItem,
  Divider,
  CardButton,
  CardContainer
} from "./style";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Tick from "../../assets/tick.svg";
import { useTranslation } from "react-i18next";

const PricingCard = ({
  title,
  price,
  status,
  feature1,
  feature2,
  feature3,
  feature4,
  feature5,
  feature6,
  highlight,
  isSelected,
  onSelect,
  mode,
  onTrialStart,
  planType, // Add planType prop to identify the plan (essential, advanced, business)
  isUpgrade = false, // New prop to indicate if this is an upgrade flow
  isDowngrade = false, // New prop to indicate if this is a downgrade flow
  onUpgradeSuccess, // Callback for successful upgrade
}) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleButtonClick = async (e) => {
    e.stopPropagation(); // Prevent card selection when clicking button

    if (price === "0") {
      const userId = localStorage.getItem("id");
      const payload = { userId };

      setLoading(true);
      try {
        const response = await axios.post(
          `${API_BASE_URL}/api/auth/update-trial-started`,
          payload
        );

        if (response.data.responseCode === 200) {
          localStorage.setItem("isTrialStarted", "true");
          onTrialStart && onTrialStart();
          navigate("/introduction");
        }
      } catch (error) {
        console.error("Error starting trial", error);
      } finally {
        setLoading(false);
      }
    } else if (isUpgrade || isDowngrade) {
      // Handle upgrade/downgrade flow with the API
      const token = localStorage.getItem("token");
      
      // Determine package_id based on planType and mode
      let newPackageId;
      if (planType === "essential") {
        newPackageId = mode === "monthly" ? "essential_monthly" : "essential_yearly";
      } else if (planType === "advanced") {
        newPackageId = mode === "monthly" ? "advanced_monthly" : "advanced_yearly";
      } else if (planType === "business") {
        newPackageId = mode === "monthly" ? "business_monthly" : "business_yearly";
      }

      if (!newPackageId) {
        console.error("Invalid package ID configuration", { planType, mode });
        alert("Invalid plan configuration");
        return;
      }

      setLoading(true);
      try {
        const apiEndpoint = isDowngrade 
          ? `${API_BASE_URL}/api/auth/subscription/downgrade`
          : `${API_BASE_URL}/api/auth/subscription/upgrade`;

        const response = await axios.post(
          apiEndpoint,
          { newPackageId },
          {
            headers: {
              "x-auth-token": token,
              "Content-Type": "application/json"
            },
          }
        );

        if (response.data.responseCode === 2000 || response.data.responseCode === 200) {
          // Update localStorage with new plan details
          if (response.data.data) {
            const { newPlan, nextInvoice } = response.data.data;
            
            if (newPlan) {
              localStorage.setItem("packageID", newPlan);
            }
            
            if (nextInvoice) {
              localStorage.setItem("nextInvoice", nextInvoice);
            }
          }
          
          // Call success callback to show snackbar and redirect
          onUpgradeSuccess && onUpgradeSuccess();
        } else {
          const errorMsg = isDowngrade 
            ? t("payment.errors.downgradeFailed") || "Failed to downgrade plan"
            : t("payment.errors.upgradeFailed") || "Failed to upgrade plan";
          alert(errorMsg);
        }
      } catch (error) {
        console.error(`Error ${isDowngrade ? 'downgrading' : 'upgrading'} plan:`, error);
        alert(t("payment.errors.errorOccurred") || "An error occurred");
      } finally {
        setLoading(false);
      }
    } else {
      // Handle regular payment flow
      const email = localStorage.getItem("email");
      
      // Determine package_id based on planType and mode
      let packageId;
      if (planType === "essential") {
        packageId = mode === "monthly" ? "essential_monthly" : "essential_yearly";
      } else if (planType === "advanced") {
        packageId = mode === "monthly" ? "advanced_monthly" : "advanced_yearly";
      } else if (planType === "business") {
        packageId = mode === "monthly" ? "business_monthly" : "business_yearly";
      }

      setLoading(true);
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/auth/session`,
          {
            params: { email, package_id: packageId },
          }
        );

        if (response.data.responseCode === 2000) {
          window.location.href = response.data.response.url;
        } else {
          alert(t("payment.errors.sessionFailed"));
        }
      } catch (error) {
        alert(t("payment.errors.errorOccurred"));
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <CardContainer
      isSelected={isSelected}
      onClick={onSelect}
      highlight={highlight}
    >
      <Card>
        {/* {highlight && (
          <div
            style={{
              position: "absolute",
              top: "-12px",
              left: "24px",
              background: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(10px)",
              color: "#ffffff",
              padding: "4px 12px",
              borderRadius: "12px",
              fontSize: "0.75rem",
              fontWeight: "600",
              border: "1px solid rgba(255, 255, 255, 0.3)",
            }}
          >
            Most Popular
          </div>
        )} */}

        <div style={{ marginBottom: "0.5rem" }}>
          <Title>{title}</Title>
          {/* <CardText>{status}</CardText> */}
        </div>

        <div style={{ margin: "1rem 0" }}>

          <div style={{ display: "flex", alignItems: "baseline", gap: "0.25rem" }}>
            <PriceText>€{price}</PriceText>
            <span
              style={{
                fontSize: "1rem",
                fontWeight: "400",
                color: "rgba(255, 255, 255, 0.7)",
              }}
            >
              {mode === "monthly" ? t("payment.plans.advanced.status") : t("payment.plans.advanced.yearly")}
            </span>
          </div>
        </div>

        <Divider />

        <Features>
          {feature1 && (
            <FeatureItem>
              <img src={Tick} alt="tick" style={{ width: "16px", height: "16px" }} />
              <span>{feature1}</span>
            </FeatureItem>
          )}
          {feature2 && (
            <FeatureItem>
              <img src={Tick} alt="tick" style={{ width: "16px", height: "16px" }} />
              <span>{feature2}</span>
            </FeatureItem>
          )}
          {feature3 && (
            <FeatureItem>
              <img src={Tick} alt="tick" style={{ width: "16px", height: "16px" }} />
              <span>{feature3}</span>
            </FeatureItem>
          )}
          {feature4 && (
            <FeatureItem>
              <img src={Tick} alt="tick" style={{ width: "16px", height: "16px" }} />
              <span>{feature4}</span>
            </FeatureItem>
          )}
          {feature5 && (
            <FeatureItem>
              <img src={Tick} alt="tick" style={{ width: "16px", height: "16px" }} />
              <span>{feature5}</span>
            </FeatureItem>
          )}
          {feature6 && (
            <FeatureItem>
              <img src={Tick} alt="tick" style={{ width: "16px", height: "16px" }} />
              <span>{feature6}</span>
            </FeatureItem>
          )}
        </Features>

        <CardButton
          onClick={handleButtonClick}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={20} style={{ color: "#51307F" }} />
          ) : (
            t("payment.getStarted")
          )}
        </CardButton>
      </Card>
    </CardContainer>
  );
};

export default PricingCard;